/* Copy da criação de plano negociado (ADR-0704): wizard de 2 passos (formulário + revisão
   read-only) antes de disparar Product + Price no Stripe — operação irreversível. Os grupos de
   campo (`plan-form-fields/*`) são componentes externos com a própria copy — fora do escopo
   desta área; aqui só o que a própria rota renderiza (cabeçalho, cards, `ReviewRow` labels,
   botões e o aviso de irreversibilidade). */
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

export interface TargetedPlanCreateContent {
  readonly breadcrumbAriaLabel: string;
  readonly breadcrumbPlans: string;
  readonly pageTitle: string;
  readonly pageDescription: string;
  readonly noPermission: string;
  readonly stepperAriaLabel: string;
  readonly stepForm: string;
  readonly stepReview: string;
  readonly createdToast: string;
  readonly form: {
    readonly identityTitle: string;
    readonly identityDescription: string;
    readonly priceTitle: string;
    readonly priceDescription: string;
    readonly targetingTitle: string;
    readonly targetingDescription: string;
    readonly seatsTitle: string;
    readonly seatsDescription: string;
    readonly capabilitiesTitle: string;
    readonly capabilitiesDescription: string;
    readonly cancel: string;
    readonly review: string;
  };
  /** Labels de `ReviewRow` do passo 2. `priceTitle`/`identityTitle`/etc. do card reusam `form.*`
      — o texto do título do card é idêntico nos dois passos. */
  readonly review: {
    readonly irreversibleWarning: string;
    readonly tier: string;
    readonly rank: string;
    readonly lookupKey: string;
    readonly displayName: string;
    readonly basePrice: string;
    readonly interval: string;
    readonly monthly: string;
    readonly yearly: string;
    readonly includedSeats: string;
    readonly extraSeatPrice: string;
    readonly targetType: string;
    readonly organization: string;
    readonly user: string;
    readonly target: string;
    readonly validFrom: string;
    readonly validUntil: string;
    readonly reason: string;
    readonly inheritsFrom: string;
    readonly none: string;
    readonly noCapabilitiesGranted: string;
    readonly yes: string;
    readonly back: string;
    readonly confirm: string;
  };
}

export const TARGETED_PLAN_CREATE_CONTENT: Bilingual<TargetedPlanCreateContent> = {
  'pt-BR': {
    breadcrumbAriaLabel: 'Trilha',
    breadcrumbPlans: 'Planos',
    pageTitle: 'Novo plano negociado',
    pageDescription:
      'Plano completo (preço e capabilities próprios) direcionado a uma organização ou usuário, ' +
      'com validade. Pode herdar o acesso a packages/templates de planos públicos.',
    noPermission: 'Você não tem permissão para criar planos.',
    stepperAriaLabel: 'Etapas da criação',
    stepForm: 'Formulário',
    stepReview: 'Revisão',
    createdToast: 'Plano negociado criado.',
    form: {
      identityTitle: 'Identidade',
      identityDescription:
        'Tier, ordenação, lookup key e nome. Lookup key e nome são imutáveis após criar.',
      priceTitle: 'Preço',
      priceDescription:
        'Preço e intervalo de cobrança. Para organizações, base e excedente zerados criam uma ' +
        'cortesia, ativada pelo owner sem cartão.',
      targetingTitle: 'Direcionamento',
      targetingDescription:
        'Alvo (organização XOR usuário), validade, motivo e herança de acesso ao marketplace.',
      seatsTitle: 'Assentos',
      seatsDescription:
        'Assentos inclusos na base e preço do assento excedente (plano de organização).',
      capabilitiesTitle: 'Capabilities',
      capabilitiesDescription:
        'O que o plano concede. Ausência da chave = não concedida (default-deny).',
      cancel: 'Cancelar',
      review: 'Revisar',
    },
    review: {
      irreversibleWarning:
        'Em planos com cobrança, criar Product + Price no Stripe é irreversível; cortesias não ' +
        'usam o provedor. Preço, intervalo, lookup key e alvo ficam imutáveis; nome e demais ' +
        'metadados continuam editáveis.',
      tier: 'Tier',
      rank: 'Ordem (rank)',
      lookupKey: 'Lookup key',
      displayName: 'Nome de exibição',
      basePrice: 'Preço base do plano',
      interval: 'Intervalo',
      monthly: 'Mensal',
      yearly: 'Anual',
      includedSeats: 'Assentos inclusos na base',
      extraSeatPrice: 'Preço do assento extra',
      targetType: 'Tipo de alvo',
      organization: 'Organização',
      user: 'Usuário',
      target: 'Alvo',
      validFrom: 'Vale de',
      validUntil: 'Vale até',
      reason: 'Motivo',
      inheritsFrom: 'Herda acesso de',
      none: 'Nenhum',
      noCapabilitiesGranted: 'Nenhuma capability concedida.',
      yes: 'Sim',
      back: 'Voltar',
      confirm: 'Criar plano negociado',
    },
  },
  'en-US': {
    breadcrumbAriaLabel: 'Breadcrumb',
    breadcrumbPlans: 'Plans',
    pageTitle: 'New targeted plan',
    pageDescription:
      'Full plan (own price and capabilities) targeted at an organization or user, with a ' +
      'validity window. Can inherit marketplace access to packages/templates from public plans.',
    noPermission: 'You do not have permission to create plans.',
    stepperAriaLabel: 'Creation steps',
    stepForm: 'Form',
    stepReview: 'Review',
    createdToast: 'Targeted plan created.',
    form: {
      identityTitle: 'Identity',
      identityDescription:
        'Tier, ordering, lookup key, and name. Lookup key and name are immutable after creation.',
      priceTitle: 'Price',
      priceDescription:
        'Price and billing interval. For organizations, a zero base and overage create a ' +
        'complimentary plan that the owner activates without a card.',
      targetingTitle: 'Targeting',
      targetingDescription:
        'Target (organization XOR user), validity, reason, and marketplace access inheritance.',
      seatsTitle: 'Seats',
      seatsDescription:
        'Seats included in the base price and the extra-seat price (organization plan).',
      capabilitiesTitle: 'Capabilities',
      capabilitiesDescription:
        'What the plan grants. Absence of the key = not granted (default-deny).',
      cancel: 'Cancel',
      review: 'Review',
    },
    review: {
      irreversibleWarning:
        'For paid plans, creating a Product + Price in Stripe is irreversible; complimentary ' +
        'plans do not use the provider. Price, interval, lookup key, and target remain immutable; ' +
        'the name and other metadata remain editable.',
      tier: 'Tier',
      rank: 'Order (rank)',
      lookupKey: 'Lookup key',
      displayName: 'Display name',
      basePrice: 'Plan base price',
      interval: 'Interval',
      monthly: 'Monthly',
      yearly: 'Yearly',
      includedSeats: 'Seats included in the base',
      extraSeatPrice: 'Extra seat price',
      targetType: 'Target type',
      organization: 'Organization',
      user: 'User',
      target: 'Target',
      validFrom: 'Valid from',
      validUntil: 'Valid until',
      reason: 'Reason',
      inheritsFrom: 'Inherits access from',
      none: 'None',
      noCapabilitiesGranted: 'No capabilities granted.',
      yes: 'Yes',
      back: 'Back',
      confirm: 'Create targeted plan',
    },
  },
};

export const useTargetedPlanCreateContent = (): TargetedPlanCreateContent =>
  useBilingual(TARGETED_PLAN_CREATE_CONTENT);
